import * as React from 'react';
import { Search } from 'lucide-react';
import { Button, FavouritesButton, Hamburger } from '@/components';
import { useFavourites } from '@/hooks';
import {
  GlobalNavigationLink,
  GlobalNavigationLinkCollection,
  GlobalNavigationLinkProps,
} from './GlobalNavigationLink';

declare interface GlobalNavigationMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  centered?: boolean;
  hasSearchIcon?: boolean;
  hasSecondaryNav?: boolean;
  globalNavigationMenuLinks: GlobalNavigationLinkCollection<GlobalNavigationLinkProps>;
}

const GlobalNavigationMenu: React.FC<GlobalNavigationMenuProps> = (props: GlobalNavigationMenuProps) => {
  const { centered, hasSearchIcon, globalNavigationMenuLinks } = props;
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { favourites } = useFavourites();
  const toggleMenu = () => {
    //If we aren't showing the hamburger button, set menu to not open and exit
    if (window.innerWidth > 1279) {
      setIsMenuOpen(false);
      return;
    }
    setIsMenuOpen(!isMenuOpen);
    // Hide all the body overflow so scrolling is only happening for the nav menu (should it take more than a screen height)
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  return (
    <div className="relative flex h-full w-full">
      <div
        className={`${isMenuOpen ? 'fixed' : 'hidden'} left-0 top-11 h-svh w-full flex-col justify-between lg:relative lg:left-[unset]
          lg:top-[unset] lg:flex lg:h-[90px] lg:overflow-y-visible`}
        onClick={toggleMenu}
      >
        <menu
          className={`flex flex-1 items-stretch ${centered ? 'lg:justify-center' : 'lg:justify-end'} h-full w-full flex-col lg:flex
            lg:flex-row ${isMenuOpen ? 'hidden' : 'hidden'} lg:relative ${!hasSearchIcon ? '' : 'lg:-right-11'} lg:mr-[-5px]`}
        >
          {globalNavigationMenuLinks.secondaryNavLinks.map((navigationLink, index) => (
            <a
              className="mt-4 text-f-xs transition-colors duration-300 ease-in-out hover:text-deep-blue lg:mr-[30px] lg:mt-[13px] last:lg:mr-0"
              key={index}
              href={navigationLink.link}
              target="_blank"
              // linkTitle={navigationLink.linkTitle}
              // tooltip={navigationLink.tooltip}
              // isDisabled={navigationLink.isDisabled}
              // isMenuOpen={isMenuOpen}
              // isNewWindow={navigationLink.isNewWindow}
            >
              {navigationLink.text}
            </a>
          ))}
        </menu>
        <menu
          className={`flex flex-1 items-stretch ${centered ? 'lg:justify-center' : 'lg:justify-end'} h-vsh w-svw flex-col lg:flex lg:h-full
            lg:w-[unset] lg:flex-row lg:space-x-8 ${isMenuOpen ? '' : 'hidden'} lg:mr-[-5px] lg:mt-[-1px]`}
        >
          {globalNavigationMenuLinks.primaryNavLinks.map((navigationLink, index) => (
            <GlobalNavigationLink
              key={index}
              text={navigationLink.text}
              link={navigationLink.link}
              linkTitle={navigationLink.linkTitle}
              tooltip={navigationLink.tooltip}
              isDisabled={navigationLink.isDisabled}
              isMenuOpen={isMenuOpen}
              isNewWindow={navigationLink.isNewWindow}
            />
          ))}
          {favourites.length > 0 && (
            <li className="hidden items-center lg:!ml-4 lg:flex">
              <FavouritesButton />
            </li>
          )}
        </menu>
      </div>
      <div className="ml-6 flex w-full items-center justify-end gap-2 lg:hidden">
        {hasSearchIcon && (
          <Button
            size="icon"
            disabled={!hasSearchIcon}
            className={`relative ${!hasSearchIcon ? 'invisible lg:visible' : ''}`}
          >
            {hasSearchIcon && <Search className="size-4" />}
          </Button>
        )}
        <FavouritesButton />

        <div className="size-block_screen_sm lg:hidden">
          <Hamburger onClick={toggleMenu} isMenuOpen={isMenuOpen} />
        </div>
      </div>
    </div>
  );
};
GlobalNavigationMenu.displayName = 'GlobalNavigationMenu';

export { GlobalNavigationMenu };
export type { GlobalNavigationMenuProps };
